/**
 * Per-route statistics used to compare candidate routes beyond "which is
 * fastest right now".
 *
 * The driver's observation: a route with fewer traffic lights is often the
 * better drive even when the ETA is a minute or two longer - it is less
 * stop-start, less tiring, and the ETA is far less volatile because you aren't
 * gambling on 40 signal cycles.
 *
 * Mapbox gives us what we need for this: every `intersections[]` entry carries
 * `traffic_signal`, `stop_sign`, `railway_crossing` and `toll_collection`
 * flags, plus an `is_urban` hint. None of it is exposed in the routing summary,
 * so we roll it up ourselves.
 *
 * Note on double counting: the `driving-traffic` profile's `duration` already
 * reflects real-world speeds, signals included. `signalDelay` here is NOT added
 * to the ETA - it exists to explain *why* two routes with similar ETAs feel
 * very different, and to rank them.
 */

import type { Route, RouteStep } from '../core/types';
import { isSignificant } from './significance';

/** Mean delay contributed per signalised junction, seconds. */
const SECONDS_PER_SIGNAL = 25;

export interface RouteStats {
  trafficSignals: number;
  stopSigns: number;
  railwayCrossings: number;
  tollBooths: number;
  /** Maneuvers that actually need a decision (minor ones merged away). */
  turns: number;
  /** Share of the route on urban roads, 0..1. */
  urbanFraction: number;
  /** Estimated seconds spent waiting at signals. Explanatory, not additive. */
  signalDelay: number;
  /** Signals per 10 km - the comparable figure between routes of unequal length. */
  signalsPerTenKm: number;
  /**
   * 0..100, higher is a smoother drive. Blends stop density and turn density;
   * used only to order and describe alternatives, never to override the ETA.
   */
  smoothness: number;
}

export function computeRouteStats(route: Route): RouteStats {
  let trafficSignals = 0;
  let stopSigns = 0;
  let railwayCrossings = 0;
  let tollBooths = 0;
  let urbanHits = 0;
  let intersectionCount = 0;
  let turns = 0;

  const steps: RouteStep[] = route.legs.flatMap((l) => l.steps);

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    if (i > 0 && isSignificant(step, steps[i - 1])) turns++;

    for (const x of step.intersections ?? []) {
      intersectionCount++;
      if (x.traffic_signal) trafficSignals++;
      if (x.stop_sign) stopSigns++;
      if (x.railway_crossing) railwayCrossings++;
      if (x.toll_collection) tollBooths++;
      if (x.is_urban) urbanHits++;
    }
  }

  const km = Math.max(0.1, route.distance / 1000);
  const signalDelay = trafficSignals * SECONDS_PER_SIGNAL;
  const signalsPerTenKm = (trafficSignals / km) * 10;
  const urbanFraction = intersectionCount ? urbanHits / intersectionCount : 0;

  // Density-based, so a long motorway run isn't punished for having more of
  // everything in absolute terms.
  const stopDensity = (trafficSignals + stopSigns * 0.6 + railwayCrossings * 1.5) / km;
  const turnDensity = turns / km;
  const smoothness = Math.round(
    Math.max(0, Math.min(100, 100 - stopDensity * 22 - turnDensity * 8)),
  );

  return {
    trafficSignals,
    stopSigns,
    railwayCrossings,
    tollBooths,
    turns,
    urbanFraction,
    signalDelay,
    signalsPerTenKm,
    smoothness,
  };
}

/** Stats for a set of candidate routes, indexed the same way. */
export function statsFor(routes: Route[]): RouteStats[] {
  return routes.map(computeRouteStats);
}

/**
 * Is route `b` worth suggesting over the current pick `a`?
 * True when it trades a small amount of time for a markedly calmer drive.
 */
export function isCalmerAlternative(
  a: { route: Route; stats: RouteStats },
  b: { route: Route; stats: RouteStats },
): boolean {
  const extraSeconds = b.route.duration - a.route.duration;
  if (extraSeconds > 420) return false; // more than 7 min slower is not a trade
  const fewerSignals = a.stats.trafficSignals - b.stats.trafficSignals;
  if (fewerSignals < 5) return false;
  // Roughly: worth it when the signals saved plausibly cover the time lost.
  return fewerSignals * SECONDS_PER_SIGNAL >= extraSeconds * 0.8;
}
